import { getDefaultModel, resolveKnownModel } from "../config/llmProviders";

export const PROVIDER_KEY_MAP = {
  openai: "openai_api_key",
  anthropic: "anthropic_api_key",
  google: "gemini_api_key",
  deepseek: "deepseek_api_key",
  groq: "groq_api_key",
  minimax: "minimax_api_key",
  openrouter: "openrouter_api_key",
};

export const DEFAULT_LLM_SETTINGS = {
  provider: "ollama",
  // Sourced from the provider config so the default model can never drift
  // from LLM_PROVIDERS.ollama.defaultModel.
  model: getDefaultModel("ollama"),
  apiKey: "",
  apiKeys: {},
  geminiThinkingLevel: "MEDIUM",
  // The corrective re-prompt in generateWorkoutPlan. On by default — it is what
  // rescues a plan whose duration or set scheme the deterministic passes could
  // not fix — but it costs a SECOND full generation, thinking included, so it
  // needs a real default and a way off rather than the implicit "anything but
  // false" it used to run on.
  refine: true,
  // How money is shown (utils/money.js). Providers bill in US$; A$ converts.
  currency: "AUD",
};

export const LLM_SYNC_FIELDS = [
  "llm_provider",
  "llm_model",
  "gemini_thinking_level",
  "llm_refine",
  "currency",
  ...Object.values(PROVIDER_KEY_MAP),
];

function getStoredApiKeys(dbSettings = {}) {
  const source = dbSettings || {};
  const apiKeys = {};

  for (const [provider, column] of Object.entries(PROVIDER_KEY_MAP)) {
    if (source[column]) {
      apiKeys[provider] = source[column];
    }
  }

  return apiKeys;
}

export function hasConfiguredLlmSettings(settings = {}) {
  const source = settings || {};
  const merged = {
    ...DEFAULT_LLM_SETTINGS,
    ...source,
    apiKeys: { ...(source.apiKeys || {}) },
  };

  const hasSavedProviderKeys = Object.keys(PROVIDER_KEY_MAP).some(
    (provider) => {
      const key = merged.apiKeys[provider];
      return typeof key === "string" ? key.trim().length > 0 : Boolean(key);
    },
  );

  return (
    merged.provider !== DEFAULT_LLM_SETTINGS.provider ||
    merged.model !== DEFAULT_LLM_SETTINGS.model ||
    hasSavedProviderKeys ||
    (merged.provider !== "ollama" && Boolean(merged.apiKey))
  );
}

export function llmSettingsToApi(settings = {}) {
  const source = settings || {};
  const merged = {
    ...DEFAULT_LLM_SETTINGS,
    ...source,
    apiKeys: { ...(source.apiKeys || {}) },
  };

  if (merged.provider !== "ollama" && merged.apiKey) {
    merged.apiKeys[merged.provider] = merged.apiKey;
  }

  const payload = {
    llm_provider: merged.provider,
    llm_model: merged.model,
    gemini_thinking_level: merged.geminiThinkingLevel,
    llm_refine: merged.refine !== false,
  };
  // Only a currency that was actually set: the A$ default must not overwrite
  // a choice saved from another device.
  if (source.currency === "USD" || source.currency === "AUD") {
    payload.currency = source.currency;
  }

  for (const [provider, column] of Object.entries(PROVIDER_KEY_MAP)) {
    if (merged.apiKeys[provider]) {
      payload[column] = merged.apiKeys[provider];
    }
  }

  return payload;
}

export function mergeFetchedLlmSettings(
  previousSettings = {},
  dbSettings = {},
) {
  const previousSource = previousSettings || {};
  const dbSource = dbSettings || {};
  const previous = {
    ...DEFAULT_LLM_SETTINGS,
    ...previousSource,
    apiKeys: { ...(previousSource.apiKeys || {}) },
  };
  const serverApiKeys = getStoredApiKeys(dbSource);
  const apiKeys = {
    ...serverApiKeys,
    ...previous.apiKeys,
  };

  if (
    previous.provider !== "ollama" &&
    previous.apiKey &&
    !apiKeys[previous.provider]
  ) {
    apiKeys[previous.provider] = previous.apiKey;
  }

  const keepLocalSelection = hasConfiguredLlmSettings(previous);
  const keepLocalThinkingLevel =
    previous.geminiThinkingLevel !== undefined &&
    previous.geminiThinkingLevel !== DEFAULT_LLM_SETTINGS.geminiThinkingLevel;
  const provider = keepLocalSelection
    ? previous.provider
    : dbSource.llm_provider ||
      previous.provider ||
      DEFAULT_LLM_SETTINGS.provider;
  // Coerced against the provider's current catalogue so a model that has since
  // been withdrawn doesn't reach the API and 404.
  const model = resolveKnownModel(
    provider,
    keepLocalSelection
      ? previous.model
      : dbSource.llm_model || previous.model || DEFAULT_LLM_SETTINGS.model,
  );
  const apiKey =
    provider === "ollama" ? "" : apiKeys[provider] || previous.apiKey || "";
  const geminiThinkingLevel = keepLocalThinkingLevel
    ? previous.geminiThinkingLevel
    : dbSource.gemini_thinking_level ||
      previous.geminiThinkingLevel ||
      DEFAULT_LLM_SETTINGS.geminiThinkingLevel;
  // Booleans can't use the `||` chain above — `false || server` would resolve a
  // deliberate opt-out back to the server's value on every fetch. Take the
  // local setting whenever one exists, then the server's, then the default.
  const refine =
    previousSource.refine ??
    (typeof dbSource.llm_refine === "boolean" ? dbSource.llm_refine : null) ??
    DEFAULT_LLM_SETTINGS.refine;

  // The account's choice wins: changing it saves straight to the account, and
  // this device's copy may only hold the A$ default.
  const currency =
    (dbSource.currency === "USD" || dbSource.currency === "AUD" ? dbSource.currency : null) ??
    previousSource.currency ??
    DEFAULT_LLM_SETTINGS.currency;

  return {
    ...DEFAULT_LLM_SETTINGS,
    ...previous,
    provider,
    model,
    apiKey,
    apiKeys,
    geminiThinkingLevel,
    refine,
    currency,
  };
}

export function shouldSyncLlmSettingsToServer(
  localSettings = {},
  dbSettings = {},
) {
  const dbSource = dbSettings || {};

  if (!hasConfiguredLlmSettings(localSettings)) {
    return false;
  }

  const localPayload = llmSettingsToApi(localSettings);
  // `|| ""` folds every falsy value together, which is right for the string
  // fields (absent and empty mean the same thing) and wrong for a boolean,
  // where `false` is a real choice rather than "unset". Compare booleans on
  // their own terms so an opt-out isn't read as "nothing to sync".
  return LLM_SYNC_FIELDS.some((field) => {
    const local = localPayload[field];
    const stored = dbSource[field];
    if (typeof local === "boolean" || typeof stored === "boolean") {
      return local !== stored;
    }
    return (local || "") !== (stored || "");
  });
}
